// ── Shared source of truth for phase agents ─────────────────────────────────
//
// These are the user-facing phase agents exposed in the onboarding wizard
// (Step5ReviewConfirm) and the per-agent model config UI (CFG.LLM.A1).
//
// NOTE: This list is intentionally NARROWER than the server-side
// `AGENT_REGISTRY` in apps/api/src/services/agentRegistry.ts, which also
// includes infrastructure agents (bootstrap, phase-gate, change-order, retro).
// The 8 below are the ones users select and configure. Never hardcode the
// count — read it from `PHASE_AGENTS.length` so adding a phase agent in the
// future stays a single-file edit.
//
// Sprint 11 UX.D1 added UX_AGENT (phase 2.5) as the 8th entry — gated on the
// shipping of UX.A1 (Phase 2.5 plumbing) and migration 0099 which expanded
// the agent_type CHECK constraint on agent_model_config / pending_config_changes
// to accept 'UX_AGENT'.

export type PhaseAgentType =
  | 'PM_AGENT'
  | 'ARCHITECTURE_AGENT'
  | 'UX_AGENT'
  | 'SPRINT_AGENT'
  | 'DEV_AGENT'
  | 'REVIEW_AGENT'
  | 'QA_AGENT'
  | 'DEVOPS_AGENT';

export interface PhaseAgentMeta {
  /**
   * SDLC phase number. Integer for whole phases (1, 2, 3, …); fractional for
   * intermediate phases like UX Design (2.5). Rendered verbatim in the UI
   * (e.g. "Phase 2.5 — UX Design Agent").
   */
  phase: number;
  /** Human-facing name shown in UI. */
  name: string;
  /**
   * Whether this agent is part of the core product tier (vs available only on
   * higher tiers). Used by the wizard to gate toggling.
   */
  core: boolean;
  /** Canonical identifier used in URLs, API payloads, and DB rows. */
  agentType: PhaseAgentType;
}

export const PHASE_AGENTS: readonly PhaseAgentMeta[] = [
  { phase: 1,   name: 'PM Agent',              core: true,  agentType: 'PM_AGENT' },
  { phase: 2,   name: 'Architecture Agent',    core: true,  agentType: 'ARCHITECTURE_AGENT' },
  { phase: 2.5, name: 'UX Design Agent',       core: true,  agentType: 'UX_AGENT' },
  { phase: 3,   name: 'Sprint Planning Agent', core: true,  agentType: 'SPRINT_AGENT' },
  { phase: 4,   name: 'Development Agent',     core: false, agentType: 'DEV_AGENT' },
  { phase: 5,   name: 'Code Review Agent',     core: false, agentType: 'REVIEW_AGENT' },
  { phase: 6,   name: 'QA Agent',              core: false, agentType: 'QA_AGENT' },
  { phase: 7,   name: 'Deployment Agent',      core: false, agentType: 'DEVOPS_AGENT' },
] as const;

/** Set of phase agent identifiers — useful for validation. */
export const PHASE_AGENT_TYPES: readonly PhaseAgentType[] =
  PHASE_AGENTS.map((a) => a.agentType);

export function isPhaseAgentType(value: string): value is PhaseAgentType {
  return (PHASE_AGENT_TYPES as readonly string[]).includes(value);
}

// ── LLM model catalogue (CFG.LLM.A1) ─────────────────────────────────────────
//
// These are the models selectable via the per-agent model config UI. Keep
// synchronised with the CHECK constraint in migration 0080_agent_model_config.sql
// — the DB enforces the same list. Adding a model = add it here AND write a
// new migration that ALTERs the CHECK constraint.

export const AVAILABLE_MODELS = [
  'claude-opus-4-6',
  'claude-sonnet-4-6',
  'claude-haiku-4-5-20251001',
] as const;

export type AvailableModel = (typeof AVAILABLE_MODELS)[number];

export function isAvailableModel(value: string): value is AvailableModel {
  return (AVAILABLE_MODELS as readonly string[]).includes(value);
}

/**
 * Default model for each phase agent when no per-tenant config row exists.
 * Chosen to match the current hardcoded choices at inference call sites:
 * sessions.ts uses Sonnet for the phase agent chat path, chat.ts uses
 * Sonnet for DEV_AGENT streaming, qa-sessions uses Sonnet. Opus is
 * reserved for the analysis-report path which isn't a phase agent.
 */
export const DEFAULT_MODEL_FOR_AGENT: Readonly<Record<PhaseAgentType, AvailableModel>> = {
  PM_AGENT:           'claude-sonnet-4-6',
  ARCHITECTURE_AGENT: 'claude-sonnet-4-6',
  UX_AGENT:           'claude-sonnet-4-6',
  SPRINT_AGENT:       'claude-sonnet-4-6',
  DEV_AGENT:          'claude-sonnet-4-6',
  REVIEW_AGENT:       'claude-sonnet-4-6',
  QA_AGENT:           'claude-sonnet-4-6',
  DEVOPS_AGENT:       'claude-sonnet-4-6',
};
